import { createTheme, type ThemeOptions } from "@mui/material/styles";

import { muiThemePalette } from "./colors";
import { muiThemeComponents } from "./components";
import { muiThemePalette as muiThemePaletteRebrand } from "./rebrand/colors";
import { muiThemePaletteDark as muiThemePaletteRebrandDark } from "./rebrand/colorsDark";
import { muiThemeTypography as muiThemeTypographyRebrand } from "./rebrand/typography";
import { muiThemeTypography } from "./typography";

const sharedThemeOptions: ThemeOptions = {
  components: muiThemeComponents,
  breakpoints: {
    values: {
      // these are the default breakpoints from MUI
      // Use useIsMobile, useIsTablet, useIsDesktop to implement responsive design
      xs: 0,
      sm: 400,
      md: 640,
      lg: 1024,
      xl: 1400,
    },
  },
  // Custom spacing scale aligned with Berlin design system
  // spacing(0)=0px, spacing(1)=2px, spacing(2)=5px, spacing(3)=10px, etc.
  spacing: [0, 2, 5, 10, 15, 20, 30, 40, 80],
  borderRadius: {
    xSmall: "3px",
    small: "6px",
    medium: "8px",
    large: "12px",
    xLarge: "16px",
    xxLarge: "30px",
    xxxLarge: "40px",
  },
  size: {
    button: {
      small: {
        height: "1.5rem", // 24px
        iconSize: "0.875rem", // 14px
        paddingX: "10px",
        paddingY: "5px",
      },
      medium: {
        height: "2.375rem", // 38px
        iconSize: "1rem", // 16px
        paddingX: "15px",
        paddingY: "10px",
      },
      large: {
        height: "3.375rem", // 54px
        iconSize: "1.5rem", // 24px
        paddingX: "15px",
        paddingY: "15px",
      },
    },
    icon: {
      small: "0.75rem", // 12px
      medium: "0.875rem", // 14px
      large: "1.125rem", // 18px
      xLarge: "1.5rem", // 24px
    },
    checkbox: {
      default: "1.25rem", // 20px (desktop)
      mobile: "1.5rem", // 24px (mobile)
      iconDefault: "0.75rem", // 12px check svg
      iconMobile: "0.875rem", // 14px check svg on mobile
    },
    avatar: {
      small: {
        size: "1.75rem", // 28px
        outlineSize: "1px",
      },
      medium: {
        size: "1.875rem", // 30px
        outlineSize: "2px",
      },
      large: {
        size: "5.625rem", // 90px
        outlineSize: "3px",
      },
    },
    tabs: {
      indicatorHeight: "3px",
      normalHeight: "2.375rem", // 38px
      largeHeight: "2.625rem", // 42px
    },
    segmentedStatusBar: {
      height: "0.625rem", // 10px
      width: "0.125rem", // 2px
    },
    drawer: {
      maxWidth: "25rem", // 400px
    },
    sidebar: {
      width: "18.75rem", // 300px
    },
    floatingNavigation: {
      height: "3.375rem", // 54px
      margin: "15px",
    },
    badge: {
      borderRadius: "20px",
    },
  },
  borderWidth: {
    regular: "1px",
    thick: "2px",
  },
  opacity: {
    disabled: 0.6,
    floatingNavigation: {
      default: 0.6,
      selected: 0.9,
    },
  },
  shadow: {
    // Standard drawer shadow - used for right-side drawers
    drawer: "0px 0px 20px 0px rgba(0, 0, 0, 0.15)",
    // Bottom sheet shadow - upward shadow for sheets appearing from bottom
    bottomSheet: "0px -4px 20px 0px rgba(0, 0, 0, 0.15)",
    // Card shadow - subtle elevation for cards
    card: "0px 4px 14px -11px rgba(0, 0, 0, 0.25)",
    // Floating navigation shadow - includes inset highlight
    floatingNavigation:
      "0px 4px 12px -6px rgba(0, 0, 0, 0.25), inset 1px 1px 1px rgba(255, 255, 255, 0.8)",
    // Modal shadow - prominent elevation for centered dialogs
    modal: "0px 0px 5px 0px rgba(0, 0, 0, 0.35), 0px 0px 50px 0px rgba(0, 0, 0, 0.65)",
  },
};

export const berlinTheme = createTheme({
  ...sharedThemeOptions,
  typography: muiThemeTypography,
  palette: muiThemePalette,
});

export const berlinRebrandTheme = createTheme({
  ...sharedThemeOptions,
  typography: muiThemeTypographyRebrand,
  palette: muiThemePaletteRebrand,
});

export const berlinRebrandThemeDark = createTheme({
  ...sharedThemeOptions,
  typography: muiThemeTypographyRebrand,
  palette: muiThemePaletteRebrandDark,
});
